import { useEffect, useState } from "react";
import { CheckCircle2, Copy, KeyRound, Laptop, LoaderCircle, Plus, Server, UserRound, XCircle } from "lucide-react";
import { api } from "../api";
import type { CliProvider, SessionUser, WorkerRecord } from "../../shared/types";

interface SettingsViewProps {
  user: SessionUser;
}

export function SettingsView({ user }: SettingsViewProps) {
  const [runtime, setRuntime] = useState<{
    provider: string;
    openAiCompatibleConfigured: boolean;
    executionMode: string;
  } | null>(null);
  const [workers, setWorkers] = useState<WorkerRecord[]>([]);
  const [enrolling, setEnrolling] = useState(false);
  const [workerToken, setWorkerToken] = useState("");

  async function loadWorkers() {
    const result = await api.workers();
    setWorkers(result.workers);
  }

  useEffect(() => {
    api.runtimeSettings().then(setRuntime).catch(console.error);
    void loadWorkers();
    const timer = window.setInterval(() => void loadWorkers(), 15_000);
    return () => window.clearInterval(timer);
  }, []);

  async function enrollWorker() {
    setEnrolling(true);
    try {
      const result = await api.enrollWorker("Mac do João");
      setWorkerToken(result.token);
      await loadWorkers();
    } finally {
      setEnrolling(false);
    }
  }

  return (
    <main className="workspace-page settings-page">
      <header className="page-header"><div><p className="eyebrow">Workspace</p><h1>Configurações</h1><p>{user.workspaceName}</p></div></header>
      <section className="settings-section">
        <div className="settings-section__title"><UserRound size={17} /><div><h2>Conta</h2><p>Sessão administrativa</p></div></div>
        <dl className="settings-list"><div><dt>Nome</dt><dd>{user.name}</dd></div><div><dt>E-mail</dt><dd>{user.email}</dd></div><div><dt>Workspace</dt><dd>{user.workspaceName}</dd></div></dl>
      </section>
      <section className="settings-section">
        <div className="settings-section__title"><Laptop size={17} /><div><h2>Workers locais</h2><p>Executam os CLIs autenticados</p></div></div>
        <div>
          <div className="worker-settings-list">
            {workers.map((worker) => (
              <div className="worker-settings-row" key={worker.id}>
                <span className={`worker-online-dot ${worker.status === "online" ? "is-online" : ""}`} />
                <div><strong>{worker.name}</strong><small>{worker.hostname || "Ainda não conectado"} · {worker.version || "sem versão"}</small></div>
                <span>{worker.status === "online" ? "Online" : "Offline"}</span>
                <div className="worker-capabilities">
                  {(["codex-cli", "claude-cli", "grok-cli", "hermes-cli"] as CliProvider[]).map((provider) => {
                    const capability = worker.capabilities[provider];
                    return <i className={capability?.ready ? "is-ready" : capability?.installed ? "is-warning" : ""} title={capability?.detail} key={provider}>{provider.replace("-cli", "")}</i>;
                  })}
                </div>
              </div>
            ))}
            {workers.length === 0 && <p className="settings-empty">Nenhum worker cadastrado.</p>}
          </div>
          <button className="button button--secondary settings-add-worker" onClick={() => void enrollWorker()} disabled={enrolling}>
            {enrolling ? <LoaderCircle className="spin" size={14} /> : <Plus size={14} />}Gerar credencial de worker
          </button>
          {workerToken && (
            <div className="worker-token">
              <div><strong>Token gerado</strong><small>Ele só será exibido agora. Guarde no arquivo protegido do worker.</small></div>
              <code>{workerToken}</code>
              <button className="icon-button" aria-label="Copiar token" title="Copiar token" onClick={() => void navigator.clipboard.writeText(workerToken)}><Copy size={15} /></button>
            </div>
          )}
        </div>
      </section>
      <section className="settings-section">
        <div className="settings-section__title"><Server size={17} /><div><h2>Runtime</h2><p>Executor principal</p></div></div>
        <dl className="settings-list"><div><dt>Modo</dt><dd><span className="status-dot status-dot--ok" />Servidor</dd></div><div><dt>Provedor padrão</dt><dd>{runtime?.provider ?? "Carregando"}</dd></div><div><dt>Processamento</dt><dd>Persistente</dd></div></dl>
      </section>
      <section className="settings-section">
        <div className="settings-section__title"><KeyRound size={17} /><div><h2>Conectores de modelo</h2><p>Variáveis protegidas no servidor</p></div></div>
        <div className="connector-row"><span className="connector-logo">AI</span><div><strong>OpenAI compatível</strong><small>Base URL, chave e modelo padrão</small></div><span className={`connector-status ${runtime?.openAiCompatibleConfigured ? "is-ready" : ""}`}>{runtime?.openAiCompatibleConfigured ? <CheckCircle2 size={14} /> : <XCircle size={14} />}{runtime?.openAiCompatibleConfigured ? "Configurado" : "Não configurado"}</span></div>
        <div className="connector-row"><span className="connector-logo connector-logo--local">DM</span><div><strong>Executor demonstrativo</strong><small>Disponível para testes do grafo</small></div><span className="connector-status is-ready"><CheckCircle2 size={14} />Pronto</span></div>
      </section>
    </main>
  );
}
